import { POINTS } from './levelCalculation';

/**
 * Care practices — the pure rules (scope 006, PR B).
 * Shared/Claude-Codex/006-mind-care-practices/HANDOVER.md.
 *
 * ⭐⭐ DERIVE, DON'T MIRROR. A practice linked to a habit stores NO completion of
 * its own while the link is open: whether it is done on a day is read from the
 * habit's `completedDates`. Completing the practice completes the habit (through
 * the habit pipeline), and undoing either undoes the one record — so the two can
 * never disagree and nothing can pay twice for one tick.
 *
 * Only two things are stored per day (`care_practice_log`):
 *  - an UNLINKED practice's completion, and
 *  - a CLOSED link's completed days, copied when the link closes (unlink,
 *    relink, habit deleted) with the habit's id — history outlives the habit.
 * Plus the optional "Not helpful today", which never touches completion or XP.
 */

export interface CarePracticeLink {
  habitId: string;
  /** Local YMD the link started. Days before it are not the practice's. */
  from: string;
  /** Local YMD the link ended (inclusive), or null while it is open. */
  to: string | null;
}

export interface CarePractice {
  id: string;
  name: string;
  /** Optional guidance shown with the practice. */
  instructions: string;
  /** Which starter it came from, if any — informational only. */
  starterKey?: string;
  /** Oldest first. At most one is open (`to === null`). */
  links: CarePracticeLink[];
  createdAt: string;
  archivedAt: string | null;
}

/** One stored day. `habitId` is set only on a day materialised from a closed link. */
export interface CareLogRow {
  practiceId: string;
  day: string;
  done: boolean;
  helpful: boolean | null;
  habitId: string | null;
}

/** The only habit fields the rules read. */
export interface HabitLike {
  id: string;
  completedDates: string[];
}

export const logKey = (practiceId: string, day: string) => `${practiceId}|${day}`;

/** The open link, or null when the practice is unlinked. */
export function openLink(p: Pick<CarePractice, 'links'>): CarePracticeLink | null {
  return p.links.find((l) => l.to === null) ?? null;
}

/** Days `from..to` (inclusive) on which a link's habit was completed. */
export function linkedDoneDays(link: CarePracticeLink, habit: HabitLike | undefined, uptoDay: string): string[] {
  if (!habit) return [];
  const end = link.to ?? uptoDay;
  return habit.completedDates.filter((d) => d >= link.from && d <= end);
}

/**
 * Is the practice done on `day`?
 * Stored rows (unlinked completions + materialised closed links) OR — only while
 * the link that covers `day` is still OPEN — the habit's own completion.
 */
export function isDoneOn(
  p: Pick<CarePractice, 'id' | 'links'>,
  day: string,
  log: Readonly<Record<string, CareLogRow>>,
  habits: ReadonlyArray<HabitLike>,
): boolean {
  if (log[logKey(p.id, day)]?.done) return true;
  const link = openLink(p);
  if (!link || day < link.from) return false;
  const habit = habits.find((h) => h.id === link.habitId);
  return !!habit && habit.completedDates.includes(day);
}

/**
 * Every day each practice was done, deduplicated per practice — a day covered
 * by a stored row AND the open link (a relink on the same day) counts once.
 */
export function doneDaysByPractice(
  practices: ReadonlyArray<Pick<CarePractice, 'id' | 'links'>>,
  log: Readonly<Record<string, CareLogRow>>,
  habits: ReadonlyArray<HabitLike>,
  today: string,
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const p of practices) out.set(p.id, new Set());
  for (const row of Object.values(log)) {
    if (row.done) out.get(row.practiceId)?.add(row.day);
  }
  for (const p of practices) {
    const link = openLink(p);
    if (!link) continue;
    const habit = habits.find((h) => h.id === link.habitId);
    for (const d of linkedDoneDays(link, habit, today)) out.get(p.id)!.add(d);
  }
  return out;
}

/**
 * Mind's care XP. Per day: `TRACKER_CARE_PRACTICE` for each practice done,
 * capped at `TRACKER_CARE_DAILY_CAP` — so adding more practices cannot farm XP.
 * ⚠️ Read-time and derived: a replayed save, a reload or a re-sync cannot add
 * XP, and undoing the habit (or the practice) takes it back.
 * ⚠️ Owner decision O2: a LINKED day earns this AND the habit's own XP, which
 * stays with the habit's domain. Archived practices keep their history and XP.
 */
export function sumCareXP(
  practices: ReadonlyArray<Pick<CarePractice, 'id' | 'links'>>,
  log: Readonly<Record<string, CareLogRow>>,
  habits: ReadonlyArray<HabitLike>,
  today: string,
): number {
  const per = POINTS.TRACKER_CARE_PRACTICE ?? 25;
  const cap = POINTS.TRACKER_CARE_DAILY_CAP ?? 50;
  const perDay = new Map<string, number>();
  for (const days of doneDaysByPractice(practices, log, habits, today).values()) {
    for (const d of days) perDay.set(d, (perDay.get(d) ?? 0) + 1);
  }
  let xp = 0;
  for (const n of perDay.values()) xp += Math.min(cap, n * per);
  return xp;
}

/** The practice already linked to this habit, if any (one practice per habit). */
export function practiceLinkedTo(
  practices: ReadonlyArray<CarePractice>,
  habitId: string,
): CarePractice | undefined {
  return practices.find((p) => !p.archivedAt && openLink(p)?.habitId === habitId);
}

/** Tolerates a missing column, `null`, and junk: a bad value must not blank the page. */
export function parsePractices(raw: unknown): CarePractice[] {
  if (!Array.isArray(raw)) return [];
  const ymd = /^\d{4}-\d{2}-\d{2}$/;
  return raw
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
    .filter((p) => typeof p.id === 'string' && typeof p.name === 'string')
    .map((p) => ({
      id: p.id as string,
      name: p.name as string,
      instructions: typeof p.instructions === 'string' ? p.instructions : '',
      starterKey: typeof p.starterKey === 'string' ? p.starterKey : undefined,
      links: Array.isArray(p.links)
        ? (p.links as unknown[])
            .filter((l): l is CarePracticeLink =>
              !!l && typeof l === 'object'
              && typeof (l as CarePracticeLink).habitId === 'string'
              && ymd.test((l as CarePracticeLink).from)
              && ((l as CarePracticeLink).to === null || ymd.test(String((l as CarePracticeLink).to))))
        : [],
      createdAt: typeof p.createdAt === 'string' ? p.createdAt : new Date(0).toISOString(),
      archivedAt: typeof p.archivedAt === 'string' ? p.archivedAt : null,
    }));
}
